import {useCallback, useMemo} from 'react';
import {
  useGetCartQuery,
  useGetPackagesQuery,
  useDeleteDiscountMutation,
  useSetDiscountMutation,
  useUpdateCartMutation,
  useCheckoutMutation,
} from '~/CreateFabricInvitation/api/endpoints/cart';

const SET_DISCOUNT_FIXED_CACHE_KEY = 'shared-set-discount';
const DELETE_DISCOUNT_FIXED_CACHE_KEY = 'shared-delete-discount';
const UPDATE_CART_FIXED_CACHE_KEY = 'shared-update-cart';
const CHECKOUT_FIXED_CACHE_KEY = 'shared-checkout';

export const useCheckoutData = () => {
  const [, checkout = {}] = useCheckoutMutation({
    fixedCacheKey: CHECKOUT_FIXED_CACHE_KEY,
  });
  return checkout.data;
};

export const useCartEndpoints = (eventId) => {
  // queries & mutations
  const {data: {transactionId = null, ...cart} = {}, ...getCart} = useGetCartQuery(eventId);
  const {data: packages = [], ...getPackages} = useGetPackagesQuery(eventId);
  const [deleteDiscountTrigger, deleteDiscount = {}] = useDeleteDiscountMutation({
    fixedCacheKey: DELETE_DISCOUNT_FIXED_CACHE_KEY,
  });
  const [setDiscountTrigger, setDiscount = {}] = useSetDiscountMutation({
    fixedCacheKey: SET_DISCOUNT_FIXED_CACHE_KEY,
  });
  const [updateCartTrigger, updateCart = {}] = useUpdateCartMutation({
    fixedCacheKey: UPDATE_CART_FIXED_CACHE_KEY,
  });
  const [checkoutTrigger, checkout = {}] = useCheckoutMutation({
    fixedCacheKey: CHECKOUT_FIXED_CACHE_KEY,
  });

  const selectedPackage = useMemo(
    () => packages.find((p) => p.packageId === cart.packageId) || packages.find((p) => p.default),
    [packages, cart?.packageId]
  );

  // status
  const isFetching = getCart.isFetching || getPackages.isFetching;
  const isLoading =
    getCart.isLoading ||
    getPackages.isLoading ||
    setDiscount.isLoading ||
    deleteDiscount.isLoading ||
    updateCart.isLoading ||
    checkout.isLoading;

  // api wrapper functions
  const handleDeleteDiscount = useCallback(
    async () => deleteDiscountTrigger({eventId, data: {transactionId}}),
    [eventId, transactionId]
  );
  const handleSetDiscount = useCallback(
    async (code) => setDiscountTrigger({eventId, data: {code, transactionId}}),
    [eventId, transactionId]
  );
  const handleUpdateCart = useCallback(
    async (data) => updateCartTrigger({eventId, data: {...data, transactionId}}),
    [eventId, transactionId]
  );
  const handleCheckout = useCallback(
    async (data) => checkoutTrigger({eventId, data: {...data, transactionId}}),
    [eventId, transactionId]
  );

  return {
    // data
    cart,
    packages,
    selectedPackage,
    checkoutData: checkout.data,
    // combined statuses
    isFetching,
    isLoading,
    // triggers
    deleteDiscount: handleDeleteDiscount,
    setDiscount: handleSetDiscount,
    updateCart: handleUpdateCart,
    checkout: handleCheckout,
    // statuses
    status: {
      getCart,
      getPackages,
      deleteDiscount,
      setDiscount,
      updateCart,
      checkout,
    },
  };
};
